import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { Achievement } from '../../types/goals';
import AchievementNotification from '../AchievementNotification';

interface AchievementNotificationContextType {
  showAchievement: (achievement: Achievement) => void;
}

const AchievementNotificationContext = createContext<AchievementNotificationContextType | undefined>(undefined);

interface AchievementNotificationProviderProps {
  children: ReactNode;
}

// Add global type declaration for our bridge
declare global {
  interface Window {
    __showAchievement?: (achievement: Achievement) => void;
  }
}

export const AchievementNotificationProvider: React.FC<AchievementNotificationProviderProps> = ({ children }) => {
  const [currentAchievement, setCurrentAchievement] = useState<Achievement | null>(null);
  const [queue, setQueue] = useState<Achievement[]>([]);
  
  const showAchievement = useCallback((achievement: Achievement) => {
    if (!currentAchievement) {
      // If no achievement is currently showing, display immediately
      setCurrentAchievement(achievement);
    } else {
      // Otherwise, add to queue
      setQueue(prev => [...prev, achievement]);
    }
  }, [currentAchievement]);
  
  const handleClose = useCallback(() => {
    setCurrentAchievement(null);
    
    // Display next achievement in queue if any
    setTimeout(() => {
      if (queue.length > 0) {
        const [next, ...rest] = queue;
        setCurrentAchievement(next);
        setQueue(rest);
      }
    }, 500);
  }, [queue]);
  
  // Create a global bridge function to allow external code to show achievements
  // This helps break circular dependencies
  useEffect(() => {
    window.__showAchievement = showAchievement;
    
    return () => {
      window.__showAchievement = undefined;
    };
  }, [showAchievement]);
  
  return (
    <AchievementNotificationContext.Provider value={{ showAchievement }}>
      {children}
      
      {currentAchievement && (
        <AchievementNotification 
          achievement={currentAchievement} 
          onClose={handleClose} 
        />
      )}
    </AchievementNotificationContext.Provider>
  );
};

export const useAchievementNotifications = (): AchievementNotificationContextType => {
  const context = useContext(AchievementNotificationContext);
  
  if (!context) {
    throw new Error(
      'useAchievementNotifications must be used within an AchievementNotificationProvider'
    );
  }
  
  return context;
};

export default AchievementNotificationProvider;